/**
 * sl-hle4.11: Per-custom-app SEO/SERP settings.
 *
 * Renders title/description/canonical/OG-image/indexing/robots-directives
 * /JSON-LD editing for one custom app at a time. View selector appears
 * only when the workspace has multiple custom apps (single-view
 * workspaces skip the selector entirely).
 *
 * The JSON-LD editor reuses `LazyCodeEditor` from the workflow-viewer
 * editor barrel — CodeMirror is the canonical editor in this repo
 * (Monaco intentionally absent). JSON syntax highlighting + onBlur
 * `JSON.parse` validation provide schema feedback without pulling a new
 * editor vendor.
 *
 * SOLID:
 *   - S: One section, one concern (per-view SEO config). View list and
 *     SEO row are separate components inside the same module.
 *   - D: Depends on GraphQLService + WorkspaceService — never touches
 *     the backend SEO service directly.
 */
import { Button, Input, Menu, MenuItem, notify } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { Upload } from '@affine/core/components/pure/file-upload';
import { useLocaleSettings } from '@affine/core/hooks/use-locale-settings';
import {
  GraphQLService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { GuardService } from '@affine/core/modules/permissions';
import { LazyCodeEditor } from '@affine/core/modules/streamlain/workflow-viewer/editor';
import { uploadWorkspaceBlob } from '@affine/core/modules/streamlain/workflow-viewer/widgets/upload-blob';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { validateAndReduceImage } from '@affine/core/utils/reduce-image';
import { UserFriendlyError } from '@affine/error';
import {
  customAppSeoQuery,
  customAppsQuery,
  updateCustomAppSeoMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

const TITLE_LIMIT = 60;
const DESCRIPTION_LIMIT = 160;

const ROBOTS_DIRECTIVE_OPTIONS = [
  'index',
  'noindex',
  'follow',
  'nofollow',
  'noai',
  'noimageai',
  'notranslate',
  'nosnippet',
] as const;

const JSON_LD_TYPES = ['auto', 'article', 'dataset', 'webpage'] as const;
const TWITTER_CARD_TYPES = ['summary', 'summary_large_image'] as const;

interface ViewListItem {
  id: string;
  title: string | null;
  slug: string;
  isPublished: boolean;
}

interface SeoSettings {
  titlesI18n: Record<string, string>;
  descriptionsI18n: Record<string, string>;
  ogImagesI18n: Record<string, string>;
  indexing: boolean;
  canonicalUrlOverride: string;
  jsonLdType: string;
  twitterCardType: string;
  robotsDirectives: string[];
  structuredDataText: string;
}

const EMPTY_SETTINGS: SeoSettings = {
  titlesI18n: {},
  descriptionsI18n: {},
  ogImagesI18n: {},
  indexing: true,
  canonicalUrlOverride: '',
  jsonLdType: 'auto',
  twitterCardType: 'summary_large_image',
  robotsDirectives: [],
  structuredDataText: '',
};

function settingsEqual(a: SeoSettings, b: SeoSettings): boolean {
  return (
    JSON.stringify(a.titlesI18n) === JSON.stringify(b.titlesI18n) &&
    JSON.stringify(a.descriptionsI18n) === JSON.stringify(b.descriptionsI18n) &&
    JSON.stringify(a.ogImagesI18n) === JSON.stringify(b.ogImagesI18n) &&
    a.indexing === b.indexing &&
    a.canonicalUrlOverride === b.canonicalUrlOverride &&
    a.jsonLdType === b.jsonLdType &&
    a.twitterCardType === b.twitterCardType &&
    JSON.stringify([...a.robotsDirectives].sort()) ===
      JSON.stringify([...b.robotsDirectives].sort()) &&
    a.structuredDataText.trim() === b.structuredDataText.trim()
  );
}

export const SeoSection: FC = () => {
  const t = useI18n();
  const graphqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceServerService = useService(WorkspaceServerService);
  const guardService = useService(GuardService);
  const workspaceId = workspaceService.workspace.id;
  const information = useWorkspaceInfo(workspaceService.workspace);
  const canEditCustomApps = useLiveData(
    guardService.can$('Workspace_CustomApps_Edit')
  );
  const isAdmin = !!(
    information?.isOwner ||
    information?.isAdmin ||
    canEditCustomApps === true
  );
  const localeSettings = useLocaleSettings();
  const defaultLocale = localeSettings.settings?.defaultLocale ?? 'en';
  const [isUploadingOgImage, setIsUploadingOgImage] = useState(false);

  const [views, setViews] = useState<ViewListItem[] | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [original, setOriginal] = useState<SeoSettings | null>(null);
  const [draft, setDraft] = useState<SeoSettings | null>(null);
  const [structuredDataError, setStructuredDataError] = useState<string | null>(
    null
  );
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [isLoadingViews, setIsLoadingViews] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadViews = useAsyncCallback(async () => {
    setIsLoadingViews(true);
    setViewsError(null);
    try {
      const result = await graphqlService.gql({
        query: customAppsQuery,
        variables: { workspaceId },
      });
      const list = result.customApps
        .filter(v => v.isPublished)
        .map(v => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          isPublished: v.isPublished,
        }));
      setViews(list);
      if (list.length > 0 && !selectedViewId) {
        setSelectedViewId(list[0].id);
      } else if (
        list.length > 0 &&
        selectedViewId &&
        !list.some(v => v.id === selectedViewId)
      ) {
        setSelectedViewId(list[0].id);
      } else if (list.length === 0) {
        setSelectedViewId(null);
      }
    } catch (err) {
      const friendly = UserFriendlyError.fromAny(err);
      setViewsError(friendly.message ?? 'Failed to load custom apps');
      setViews([]);
    } finally {
      setIsLoadingViews(false);
    }
  }, [graphqlService, workspaceId, selectedViewId]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const loadSettings = useAsyncCallback(
    async (customAppId: string) => {
      setIsLoadingSettings(true);
      setSettingsError(null);
      setStructuredDataError(null);
      try {
        const result = await graphqlService.gql({
          query: customAppSeoQuery,
          variables: { customAppId, workspaceId },
        });
        const seo = result.customAppSEO;
        if (!seo) {
          setOriginal({ ...EMPTY_SETTINGS });
          setDraft({ ...EMPTY_SETTINGS });
          return;
        }
        const titles = (seo.titlesI18n ?? {}) as Record<string, string>;
        const descriptions = (seo.descriptionsI18n ?? {}) as Record<
          string,
          string
        >;
        const ogImages = (seo.ogImagesI18n ?? {}) as Record<string, string>;
        const structured =
          seo.structuredData != null
            ? JSON.stringify(seo.structuredData, null, 2)
            : '';
        const next: SeoSettings = {
          titlesI18n: titles,
          descriptionsI18n: descriptions,
          ogImagesI18n: ogImages,
          indexing: seo.indexing ?? true,
          canonicalUrlOverride: seo.canonicalUrlOverride ?? '',
          jsonLdType: seo.jsonLdType ?? 'auto',
          twitterCardType: seo.twitterCardType ?? 'summary_large_image',
          robotsDirectives: [...seo.robotsDirectives],
          structuredDataText: structured,
        };
        setOriginal(next);
        setDraft({ ...next });
      } catch (err) {
        const friendly = UserFriendlyError.fromAny(err);
        const message = friendly.message ?? String(err);
        setSettingsError(message);
        setOriginal(null);
        setDraft(null);
      } finally {
        setIsLoadingSettings(false);
      }
    },
    [graphqlService, workspaceId]
  );

  useEffect(() => {
    if (selectedViewId) {
      loadSettings(selectedViewId);
    }
  }, [selectedViewId, loadSettings]);

  const dirty = useMemo(
    () => !!(original && draft && !settingsEqual(original, draft)),
    [original, draft]
  );

  const setDraftField = useCallback(
    <K extends keyof SeoSettings>(key: K, value: SeoSettings[K]) => {
      setDraft(prev => (prev ? { ...prev, [key]: value } : prev));
    },
    []
  );

  const setLocaleField = useCallback(
    (
      mapKey: 'titlesI18n' | 'descriptionsI18n' | 'ogImagesI18n',
      locale: string,
      value: string
    ) => {
      setDraft(prev => {
        if (!prev) return prev;
        const next = { ...prev[mapKey] };
        if (value.length === 0) {
          delete next[locale];
        } else {
          next[locale] = value;
        }
        return { ...prev, [mapKey]: next };
      });
    },
    []
  );

  const handleOgImageUpload = useAsyncCallback(
    async (file: File) => {
      const baseUrl = workspaceServerService.server?.baseUrl ?? location.origin;
      setIsUploadingOgImage(true);
      try {
        const reduced = await validateAndReduceImage(file);
        const uploaded = await uploadWorkspaceBlob(workspaceId, reduced);
        const url = `${baseUrl}/api/custom-app-assets/${workspaceId}/${uploaded.blobId}`;
        setLocaleField('ogImagesI18n', defaultLocale, url);
      } catch (err) {
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.seo.ogImage.uploadFailed'
            ](),
          message: UserFriendlyError.fromAny(err).message ?? String(err),
        });
      } finally {
        setIsUploadingOgImage(false);
      }
    },
    [workspaceServerService, workspaceId, defaultLocale, setLocaleField, t]
  );

  const toggleRobotsDirective = useCallback((directive: string) => {
    setDraft(prev => {
      if (!prev) return prev;
      const has = prev.robotsDirectives.includes(directive);
      const next = has
        ? prev.robotsDirectives.filter(d => d !== directive)
        : [...prev.robotsDirectives, directive];
      return { ...prev, robotsDirectives: next };
    });
  }, []);

  const validateStructuredData = useCallback(
    (text: string): { ok: boolean; parsed: object | null; error?: string } => {
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return { ok: true, parsed: null };
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed !== 'object' || parsed === null) {
          return {
            ok: false,
            parsed: null,
            error:
              t[
                'com.streamlain.settings.workspace.customApps.seo.jsonld.notObject'
              ](),
          };
        }
        return { ok: true, parsed: parsed as object };
      } catch (err) {
        return {
          ok: false,
          parsed: null,
          error: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [t]
  );

  const handleStructuredBlur = useCallback(() => {
    if (!draft) return;
    const { ok, error } = validateStructuredData(draft.structuredDataText);
    setStructuredDataError(ok ? null : (error ?? 'Invalid JSON'));
  }, [draft, validateStructuredData]);

  const handleSave = useAsyncCallback(async () => {
    if (!isAdmin || !draft || !selectedViewId) return;
    const validation = validateStructuredData(draft.structuredDataText);
    if (!validation.ok) {
      setStructuredDataError(validation.error ?? 'Invalid JSON');
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.customApps.seo.jsonld.invalid'
          ](),
        message: validation.error ?? '',
      });
      return;
    }
    setIsSaving(true);
    try {
      await graphqlService.gql({
        query: updateCustomAppSeoMutation,
        variables: {
          input: {
            customAppId: selectedViewId,
            workspaceId,
            titlesI18n: draft.titlesI18n,
            descriptionsI18n: draft.descriptionsI18n,
            ogImagesI18n: draft.ogImagesI18n,
            indexing: draft.indexing,
            canonicalUrlOverride:
              draft.canonicalUrlOverride.trim().length === 0
                ? null
                : draft.canonicalUrlOverride.trim(),
            jsonLdType: draft.jsonLdType,
            twitterCardType: draft.twitterCardType,
            robotsDirectives: draft.robotsDirectives,
            structuredData: validation.parsed,
          },
        },
      });
      notify.success({
        title:
          t['com.streamlain.settings.workspace.customApps.seo.savedToast'](),
      });
      loadSettings(selectedViewId);
    } catch (err) {
      notify.error({
        title:
          t['com.streamlain.settings.workspace.customApps.seo.saveFailed'](),
        message: UserFriendlyError.fromAny(err).message ?? String(err),
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isAdmin,
    draft,
    selectedViewId,
    workspaceId,
    graphqlService,
    validateStructuredData,
    loadSettings,
    t,
  ]);

  const sectionTitle =
    t['com.streamlain.settings.workspace.customApps.seo.title']();

  if (isLoadingViews) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t['com.streamlain.settings.workspace.customApps.seo.loading']()}
          desc=""
        />
      </SettingWrapper>
    );
  }

  if (viewsError) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.seo.viewsError'
          ]()}
          desc={viewsError}
        />
      </SettingWrapper>
    );
  }

  if (!views || views.length === 0) {
    return (
      <SettingWrapper title={sectionTitle}>
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.seo.empty.title'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.seo.empty.desc'
          ]()}
        />
      </SettingWrapper>
    );
  }

  const selectedView = views.find(v => v.id === selectedViewId) ?? null;
  const showSelector = views.length > 1;

  const titleValue = draft?.titlesI18n[defaultLocale] ?? '';
  const descriptionValue = draft?.descriptionsI18n[defaultLocale] ?? '';
  const ogImageValue = draft?.ogImagesI18n[defaultLocale] ?? '';

  return (
    <SettingWrapper title={sectionTitle}>
      {showSelector ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.seo.selector.label'
          ]()}
          desc={t[
            'com.streamlain.settings.workspace.customApps.seo.selector.desc'
          ]()}
        >
          <Menu
            items={views.map(view => (
              <MenuItem
                key={view.id}
                onClick={() => setSelectedViewId(view.id)}
                selected={view.id === selectedViewId}
              >
                {view.title?.length ? view.title : view.slug}
              </MenuItem>
            ))}
          >
            <Button>
              {selectedView?.title?.length
                ? selectedView.title
                : (selectedView?.slug ?? '—')}
            </Button>
          </Menu>
        </SettingRow>
      ) : null}

      {isLoadingSettings ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.seo.loadingSettings'
          ]()}
          desc=""
        />
      ) : settingsError ? (
        <SettingRow
          name={t[
            'com.streamlain.settings.workspace.customApps.seo.loadFailed'
          ]()}
          desc={settingsError}
        >
          <Button onClick={() => loadSettings(selectedViewId!)}>
            {t['com.streamlain.settings.workspace.customApps.retry']()}
          </Button>
        </SettingRow>
      ) : !draft ? null : (
        <>
          <SerpPreviewRow
            title={titleValue}
            description={descriptionValue}
            slug={selectedView?.slug ?? ''}
          />

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.title.label'
            ]()}
            desc={
              t[
                'com.streamlain.settings.workspace.customApps.seo.title.desc'
              ]?.({
                limit: String(TITLE_LIMIT),
              }) ??
              `Default-locale title. Recommended max ${TITLE_LIMIT} chars.`
            }
          >
            <CharCountInput
              value={titleValue}
              onChange={value =>
                setLocaleField('titlesI18n', defaultLocale, value)
              }
              limit={TITLE_LIMIT}
              disabled={!isAdmin || isSaving}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.description.label'
            ]()}
            desc={
              t[
                'com.streamlain.settings.workspace.customApps.seo.description.desc'
              ]?.({
                limit: String(DESCRIPTION_LIMIT),
              }) ??
              `Default-locale description. Recommended max ${DESCRIPTION_LIMIT} chars.`
            }
          >
            <CharCountInput
              value={descriptionValue}
              onChange={value =>
                setLocaleField('descriptionsI18n', defaultLocale, value)
              }
              limit={DESCRIPTION_LIMIT}
              disabled={!isAdmin || isSaving}
              multiline
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.ogImage.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.ogImage.desc'
            ]()}
            spreadCol={false}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
              }}
            >
              <Input
                value={ogImageValue}
                onChange={value =>
                  setLocaleField('ogImagesI18n', defaultLocale, value)
                }
                disabled={!isAdmin || isSaving || isUploadingOgImage}
                placeholder="https://…"
                style={{ minWidth: 280 }}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Upload
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  fileChange={handleOgImageUpload}
                  disabled={!isAdmin || isSaving || isUploadingOgImage}
                  data-testid="seo-og-image-upload"
                >
                  <Button
                    variant="secondary"
                    disabled={!isAdmin || isSaving || isUploadingOgImage}
                  >
                    {isUploadingOgImage
                      ? t[
                          'com.streamlain.settings.workspace.customApps.seo.ogImage.uploading'
                        ]()
                      : t[
                          'com.streamlain.settings.workspace.customApps.seo.ogImage.upload'
                        ]()}
                  </Button>
                </Upload>
                {ogImageValue.length > 0 ? (
                  <Button
                    variant="plain"
                    disabled={!isAdmin || isSaving || isUploadingOgImage}
                    onClick={() =>
                      setLocaleField('ogImagesI18n', defaultLocale, '')
                    }
                  >
                    {t[
                      'com.streamlain.settings.workspace.customApps.seo.ogImage.clear'
                    ]()}
                  </Button>
                ) : null}
              </div>
            </div>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.canonical.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.canonical.desc'
            ]()}
          >
            <Input
              value={draft.canonicalUrlOverride}
              onChange={value => setDraftField('canonicalUrlOverride', value)}
              disabled={!isAdmin || isSaving}
              placeholder="https://example.com/page"
              style={{ minWidth: 280 }}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.indexing.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.indexing.desc'
            ]()}
          >
            <Button
              variant={draft.indexing ? 'primary' : 'secondary'}
              disabled={!isAdmin || isSaving}
              onClick={() => setDraftField('indexing', !draft.indexing)}
            >
              {draft.indexing
                ? t[
                    'com.streamlain.settings.workspace.customApps.seo.indexing.on'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.customApps.seo.indexing.off'
                  ]()}
            </Button>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.robots.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.robots.desc'
            ]()}
            spreadCol={false}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ROBOTS_DIRECTIVE_OPTIONS.map(directive => {
                const checked = draft.robotsDirectives.includes(directive);
                return (
                  <DirectiveChip
                    key={directive}
                    label={directive}
                    checked={checked}
                    disabled={!isAdmin || isSaving}
                    onToggle={() => toggleRobotsDirective(directive)}
                  />
                );
              })}
            </div>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.jsonldType.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.jsonldType.desc'
            ]()}
          >
            <Menu
              items={JSON_LD_TYPES.map(type => (
                <MenuItem
                  key={type}
                  onClick={() => setDraftField('jsonLdType', type)}
                  selected={type === draft.jsonLdType}
                >
                  {type}
                </MenuItem>
              ))}
            >
              <Button disabled={!isAdmin || isSaving}>
                {draft.jsonLdType}
              </Button>
            </Menu>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.twitterCard.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.twitterCard.desc'
            ]()}
          >
            <Menu
              items={TWITTER_CARD_TYPES.map(type => (
                <MenuItem
                  key={type}
                  onClick={() => setDraftField('twitterCardType', type)}
                  selected={type === draft.twitterCardType}
                >
                  {type}
                </MenuItem>
              ))}
            >
              <Button disabled={!isAdmin || isSaving}>
                {draft.twitterCardType}
              </Button>
            </Menu>
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.seo.jsonld.label'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.seo.jsonld.desc'
            ]()}
            spreadCol={false}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                width: '100%',
              }}
            >
              <div
                onBlur={handleStructuredBlur}
                style={{
                  border: structuredDataError
                    ? '1px solid var(--streamlain-error-color)'
                    : '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <LazyCodeEditor
                  value={draft.structuredDataText}
                  onChange={value => setDraftField('structuredDataText', value)}
                  language="json"
                  readOnly={!isAdmin || isSaving}
                  placeholder='{"@context": "https://schema.org", "@type": "Article"}'
                />
              </div>
              {structuredDataError ? (
                <div
                  role="alert"
                  style={{
                    color: 'var(--streamlain-error-color)',
                    fontSize: 12,
                  }}
                >
                  {structuredDataError}
                </div>
              ) : null}
            </div>
          </SettingRow>

          {isAdmin ? (
            <SettingRow name="" desc="">
              <Button
                variant="primary"
                disabled={!dirty || isSaving}
                onClick={handleSave}
              >
                {isSaving
                  ? t[
                      'com.streamlain.settings.workspace.customApps.seo.savingAction'
                    ]()
                  : t[
                      'com.streamlain.settings.workspace.customApps.seo.saveAction'
                    ]()}
              </Button>
            </SettingRow>
          ) : null}
        </>
      )}
    </SettingWrapper>
  );
};

interface CharCountInputProps {
  value: string;
  onChange: (next: string) => void;
  limit: number;
  disabled: boolean;
  multiline?: boolean;
}

const CharCountInput: FC<CharCountInputProps> = ({
  value,
  onChange,
  limit,
  disabled,
  multiline,
}) => {
  const length = value.length;
  const overLimit = length > limit;
  const inLimitWithContent = !overLimit && length > 0;
  const counterColor = overLimit
    ? 'var(--streamlain-warning-color)'
    : inLimitWithContent
      ? 'var(--streamlain-success-color)'
      : 'var(--streamlain-v2-text-tertiary)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          rows={3}
          style={{
            minWidth: 280,
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
            background: 'var(--streamlain-v2-layer-background-primary)',
            color: 'var(--streamlain-v2-text-primary)',
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
      ) : (
        <Input
          value={value}
          onChange={onChange}
          disabled={disabled}
          style={{ minWidth: 280 }}
        />
      )}
      <div
        style={{
          fontSize: 11,
          color: counterColor,
          alignSelf: 'flex-end',
        }}
      >
        {length} / {limit}
      </div>
    </div>
  );
};

interface DirectiveChipProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const DirectiveChip: FC<DirectiveChipProps> = ({
  label,
  checked,
  disabled,
  onToggle,
}) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      data-checked={checked || undefined}
      style={{
        padding: '4px 10px',
        borderRadius: 16,
        border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
        background: checked
          ? 'var(--streamlain-v2-layer-background-primary)'
          : 'var(--streamlain-v2-layer-background-secondary)',
        color: checked
          ? 'var(--streamlain-v2-text-primary)'
          : 'var(--streamlain-v2-text-secondary)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
};

interface SerpPreviewRowProps {
  title: string;
  description: string;
  slug: string;
}

const SerpPreviewRow: FC<SerpPreviewRowProps> = ({
  title,
  description,
  slug,
}) => {
  const t = useI18n();
  const [debouncedTitle, setDebouncedTitle] = useState(title);
  const [debouncedDescription, setDebouncedDescription] = useState(description);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTitle(title), 300);
    return () => clearTimeout(handle);
  }, [title]);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedDescription(description), 300);
    return () => clearTimeout(handle);
  }, [description]);
  const untitledLabel =
    t['com.streamlain.settings.workspace.customApps.seo.preview.untitled']();
  const placeholderDescription =
    t[
      'com.streamlain.settings.workspace.customApps.seo.preview.placeholderDescription'
    ]();
  const displayTitle =
    debouncedTitle.length > 0 ? debouncedTitle : untitledLabel;
  const displayDescription =
    debouncedDescription.length > 0
      ? debouncedDescription
      : placeholderDescription;
  const displayUrl =
    slug.length > 0 ? `https://example.com/${slug}` : 'https://example.com/…';
  return (
    <SettingRow
      name={t[
        'com.streamlain.settings.workspace.customApps.seo.preview.label'
      ]()}
      desc={t[
        'com.streamlain.settings.workspace.customApps.seo.preview.desc'
      ]()}
      spreadCol={false}
    >
      <div
        aria-label="serp-preview"
        style={{
          background: 'var(--streamlain-v2-layer-background-primary)',
          border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
          borderRadius: 8,
          padding: 12,
          maxWidth: 600,
          fontFamily: 'arial, sans-serif',
        }}
      >
        <div style={{ fontSize: 12, color: '#202124' }}>{displayUrl}</div>
        <div
          style={{
            color: '#1a0dab',
            fontSize: 18,
            fontWeight: 400,
            marginTop: 2,
            lineHeight: '24px',
          }}
        >
          {displayTitle}
        </div>
        <div
          style={{
            color: '#4d5156',
            fontSize: 13,
            lineHeight: '18px',
            marginTop: 4,
          }}
        >
          {displayDescription}
        </div>
      </div>
    </SettingRow>
  );
};
